import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PiMagnifyingGlass, PiPencilSimpleLine } from 'react-icons/pi'

interface InboxSearchBarProps {
  value: string
  onChange: (value: string) => void
  onCompose?: () => void
}

/** Debounced search input with a Compose button. */
export function InboxSearchBar({
  value,
  onChange,
  onCompose,
}: InboxSearchBarProps) {
  const [localValue, setLocalValue] = useState(value)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setLocalValue(newValue)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        onChange(newValue)
      }, 300)
    },
    [onChange]
  )

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="flex items-center gap-2.5">
      <div className="relative flex-1">
        <PiMagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 size-[22px] text-fg-secondary" />
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          placeholder="Search mail by company name, domain, sender"
          className={cn(
            'w-full pl-11 pr-3 h-9 text-sm rounded-lg border border-line',
            'placeholder:text-fg-secondary focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand'
          )}
        />
      </div>
      <FbButton
        variant="primary"
        disabled={!onCompose}
        onClick={onCompose}
        className="!min-h-0 h-9 px-2 gap-2"
      >
        <PiPencilSimpleLine className="size-[22px]" />
        <span className="text-sm font-medium">Compose</span>
      </FbButton>
    </div>
  )
}
